import React from 'react';
import i18next from 'i18next';
import { getCf, getCf2 } from '../../../utils';
import {
  CRASH_STATUS_BETTING,
  CRASH_STATUS_ENDED,
  CRASH_STATUS_IN_GAME,
} from '../CrashGame';

function stepValues(x) {
  let c = 0.4;
  let r = 0.1;
  while (true) {
    if (x < c) return r;
    c *= 5;
    r *= 2;
    if (x < c) return r;
    c *= 2;
    r *= 5;
  }
}

export default class Graph {
  constructor(width, height) {
    this.countdown = 10000;

    this.canvasWidth = width;
    this.canvasHeight = height;

    //Plotting Settings
    this.plotWidth = this.canvasWidth - 30;
    this.plotHeight = this.canvasHeight - 20;
    this.xStart = this.canvasWidth - this.plotWidth;
    this.XAxisPlotMinValue = 10000;
    this.YAxisSizeMultiplier = 2;
  }

  setData(ctx, canvas, gameState) {
    this.ctx = ctx;
    this.canvas = canvas;

    this.gradient = this.ctx.createLinearGradient(
      0,
      this.canvasHeight,
      this.canvasWidth,
      0
    );
    this.gradient.addColorStop(0, '#16D0EA');
    this.gradient.addColorStop(1.0, '#9248EF');

    const now = Date.now();

    if (!this.gameState || gameState.status !== this.gameState.status) {
      this.elapsed =
        gameState.status === CRASH_STATUS_IN_GAME ? gameState.elapsed : 0;
      this.phaseStartedAt = gameState.current_time * 1000;
      this.last_tick = now;
    } else {
      this.elapsed += now - this.last_tick;
    }

    this.lastBalance = getCf(this.currentTime);

    if (gameState.status === CRASH_STATUS_BETTING) {
      this.currentTime = 0;
      this.countdown = Math.max(
        gameState.next_round_at * 1000 - (this.phaseStartedAt + this.elapsed),
        0
      );
    }

    if (gameState.status === CRASH_STATUS_IN_GAME) {
      if (now - this.last_tick > 1000) {
        this.elapsed = gameState.elapsed;
      }

      this.currentTime = this.elapsed;
    }

    if (gameState.status === CRASH_STATUS_ENDED) {
      this.currentTime = gameState.elapsed;
      this.lastBalance = gameState.cf;
    }

    this.gameState = gameState;
    this.last_tick = now;
  }

  resize(width) {
    this.canvasWidth = width;
    this.plotWidth = this.canvasWidth - 20;
    this.xStart = this.canvasWidth - this.plotWidth;
  }

  clean = function () {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  };

  drawGameData() {
    const ctx = this.ctx;
    const { status } = this.gameState;

    if (status === CRASH_STATUS_IN_GAME) {
      ctx.fillStyle = '#ffffff';
      ctx.font = '600 48px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(
        parseFloat(this.lastBalance).toFixed(2) + 'x',
        this.canvasWidth / 2,
        this.canvasHeight / 2 + 10
      );
    }

    if (status === CRASH_STATUS_ENDED) {
      const cf = parseFloat(this.lastBalance).toFixed(2);
      ctx.font = '600 54px monospace';
      ctx.textAlign = 'center';
      ctx.fillStyle = cf >= 7 ? '#f4c753' : cf >= 1.3 ? '#1ec8eb' : '#f45353';
      ctx.fillText(cf + 'x', this.canvasWidth / 2, this.canvasHeight / 2 + 15);
    }

    if (status === CRASH_STATUS_BETTING) {
      ctx.font = '600 38px montserrat';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#f4c753';

      ctx.fillText(
        i18next.t('crash.countdown', {
          sec: (this.countdown / 1000).toFixed(1),
        }),
        this.canvasWidth / 2,
        this.canvasHeight / 2
      );

      ctx.font = '600 20px montserrat';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(
        i18next.t('crash.countdown_remaining'),
        this.canvasWidth / 2,
        this.canvasHeight / 2 + 30
      );
    }
  }

  calculatePlotValues() {
    //Plot variables
    this.YAxisPlotMinValue = this.YAxisSizeMultiplier;
    this.YAxisPlotValue = this.YAxisPlotMinValue;

    this.XAxisPlotValue = this.XAxisPlotMinValue;

    //Adjust X Plot's Axis
    if (this.currentTime > this.XAxisPlotMinValue)
      this.XAxisPlotValue = this.currentTime;

    //Adjust Y Plot's Axis
    if (this.lastBalance > this.YAxisPlotMinValue)
      this.YAxisPlotValue = this.lastBalance;

    //We start counting from cero to plot
    this.YAxisPlotValue -= 1;

    //Graph values
    this.widthIncrement = this.plotWidth / this.XAxisPlotValue;
    this.heightIncrement = this.plotHeight / this.YAxisPlotValue;
  }

  drawGraph() {
    /* Style the line depending on the game states */
    this.ctx.strokeStyle = 'Black';
    this.ctx.lineWidth = 4;

    /*my */
    this.ctx.setLineDash([]);
    this.ctx.fillStyle = this.gradient;
    this.ctx.strokeStyle = this.gradient;
    this.ctx.beginPath();
    this.ctx.fill();

    /* Draw the graph */
    for (let t = 0, i = 0; t <= this.currentTime; t += 10, i++) {
      /* Graph */
      const payout = getCf2(t) - 1; //We start counting from one x
      const y = this.plotHeight - payout * this.heightIncrement;
      const x = t * this.widthIncrement;
      this.ctx.lineTo((x + this.xStart).toFixed(2), y.toFixed(2));

      /* Avoid crashing the explorer if the cycle is infinite */
      if (i > 50000) {
        console.log('For 1 too long!');
        break;
      }
    }
    this.ctx.stroke();
  }

  drawIndexes() {
    this.payoutSeparation = stepValues(
      !this.lastBalance ? 1 : this.lastBalance
    );

    this.ctx.lineWidth = 1;
    this.ctx.strokeStyle = '#5a5880';
    this.ctx.font = '12px montserrat';
    this.ctx.fillStyle = '#5a5880';
    //Draw Y Axis Values
    const heightIncrement = this.plotHeight / this.YAxisPlotValue;
    for (
      let payout = this.payoutSeparation, i = 0;
      payout < this.YAxisPlotValue;
      payout += this.payoutSeparation, i++
    ) {
      const y = this.plotHeight - payout * heightIncrement;
      this.ctx.fillText(payout + 1 + 'x', 20, y);
      if (i > 100) {
        console.log('For 3 too long');
        break;
      }
    }

    //Calculate X Axis
    this.milisecondsSeparation = stepValues(this.XAxisPlotValue);
    this.XAxisValuesSeparation =
      this.plotWidth / (this.XAxisPlotValue / this.milisecondsSeparation);
    //Draw X Axis Values
    for (
      let milliseconds = 0, counter = 0, i = 0;
      milliseconds < this.XAxisPlotValue;
      milliseconds += this.milisecondsSeparation, counter++, i++
    ) {
      const seconds = (milliseconds / 1000).toString();
      const textWidth = this.ctx.measureText(seconds).width;
      const x = counter * this.XAxisValuesSeparation + this.xStart;
      this.ctx.fillText(seconds, x - textWidth / 2, this.plotHeight + 11);

      if (i > 100) {
        console.log('For 4 too long');
        break;
      }
    }
  }

  drawAxes() {
    this.payoutSeparation = stepValues(
      !this.lastBalance ? 1 : this.lastBalance
    );
    this.ctx.setLineDash([5, 5]);
    this.ctx.lineWidth = 1;
    this.ctx.strokeStyle = '#202039';
    this.ctx.fillStyle = '#202039';

    //Draw Y Axis Values
    const heightIncrement = this.plotHeight / this.YAxisPlotValue;
    for (
      let payout = this.payoutSeparation, i = 0;
      payout < this.YAxisPlotValue;
      payout += this.payoutSeparation, i++
    ) {
      const y = this.plotHeight - payout * heightIncrement;
      this.ctx.beginPath();
      this.ctx.moveTo(this.xStart + 35, y);
      this.ctx.lineTo(this.xStart + this.canvasWidth, y);
      this.ctx.stroke();

      if (i > 100) {
        console.log('For 3 too long');
        break;
      }
    }
  }
}



// WEBPACK FOOTER //
// ./app/components/CrashGame/Graph/Graph.jsx